import React, { useState, useEffect } from 'react';
import { AdminSidebar } from './AdminDashboard';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_OPTIONS = [
  { value: 'nuevo', label: 'Nuevo', color: 'bg-blue-500' },
  { value: 'contactado', label: 'Contactado', color: 'bg-yellow-500' },
  { value: 'interesado', label: 'Interesado', color: 'bg-purple-500' },
  { value: 'convertido', label: 'Convertido', color: 'bg-green-500' },
  { value: 'perdido', label: 'Perdido', color: 'bg-red-500' },
];

export default function AdminLeads({ user }) {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    fetchLeads();
  }, []);
  
  const fetchLeads = async () => {
    try {
      const response = await fetch(`${API}/api/leads`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setLeads(data);
      }
    } catch (error) {
      console.error('Error fetching leads:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const updateStatus = async (leadId, newStatus) => {
    try {
      const response = await fetch(`${API}/api/leads/${leadId}?status=${newStatus}`, {
        method: 'PATCH',
        credentials: 'include'
      });
      
      if (response.ok) {
        setLeads(leads.map(lead => 
          lead.lead_id === leadId ? { ...lead, status: newStatus } : lead
        ));
        toast.success('Estado actualizado');
      }
    } catch (error) {
      toast.error('Error al actualizar estado');
    }
  };
  
  const getStatusBadge = (status) => {
    const statusOption = STATUS_OPTIONS.find(s => s.value === status);
    return (
      <Badge className={`${statusOption?.color || 'bg-zinc-500'} text-white hover:opacity-80`}>
        {statusOption?.label || status}
      </Badge>
    );
  };
  
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('es-CO', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  return (
    <div className="min-h-screen bg-[#050505]">
      <AdminSidebar user={user} />
      
      <main className="ml-64 p-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold" data-testid="leads-title">Leads</h1>
          <p className="text-zinc-500 mt-1">Gestiona las solicitudes de demo</p>
        </div>
        
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-white/20 border-t-white rounded-full animate-spin" />
          </div>
        ) : leads.length === 0 ? (
          <div className="bg-[#0A0A0A] border border-white/5 p-12 text-center">
            <p className="text-zinc-500">No hay leads todavía</p>
          </div>
        ) : (
          <div className="bg-[#0A0A0A] border border-white/5">
            <Table>
              <TableHeader>
                <TableRow className="border-white/5 hover:bg-transparent">
                  <TableHead className="text-zinc-400">Negocio</TableHead>
                  <TableHead className="text-zinc-400">Industria</TableHead>
                  <TableHead className="text-zinc-400">Teléfono</TableHead>
                  <TableHead className="text-zinc-400">Fecha</TableHead>
                  <TableHead className="text-zinc-400">Estado</TableHead>
                  <TableHead className="text-zinc-400">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {leads.map((lead) => (
                  <TableRow key={lead.lead_id} className="border-white/5" data-testid={`lead-row-${lead.lead_id}`}>
                    <TableCell className="font-medium">{lead.business_name}</TableCell>
                    <TableCell className="text-zinc-400">{lead.industry}</TableCell>
                    <TableCell className="text-zinc-400">
                      <a 
                        href={`https://wa.me/${lead.phone.replace(/\D/g, '')}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:text-white transition-colors"
                      >
                        {lead.phone}
                      </a>
                    </TableCell>
                    <TableCell className="text-zinc-400">{formatDate(lead.created_at)}</TableCell>
                    <TableCell>{getStatusBadge(lead.status)}</TableCell>
                    <TableCell>
                      <Select
                        value={lead.status}
                        onValueChange={(value) => updateStatus(lead.lead_id, value)}
                      >
                        <SelectTrigger className="w-32 bg-transparent border-white/10" data-testid={`status-select-${lead.lead_id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#0A0A0A] border-white/10">
                          {STATUS_OPTIONS.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </main>
    </div>
  );
}
